import { EmployeeUser } from '../types/admin_types';
import { JobRequisition } from '../types/JobOpenings';

/**
 * Formats a timestamp or date string into YYYY-MM-DD format
 */
const formatDeadlineDate = (deadline: number | string): string => {
  if (typeof deadline === 'number') {
    // Convert seconds to milliseconds if needed
    const ms = deadline * (deadline < 1e12 ? 1000 : 1);
    const date = new Date(ms);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  }
  return deadline;
};

/**
 * Converts a mixed array of strings/objects to an array of strings (IDs)
 */
const extractStringIds = (arr: (string | EmployeeUser | any)[]): string[] => {
  if (!Array.isArray(arr)) return [];

  return arr.map((item: any) => {
    if (typeof item === 'string') {
      return item;
    }
    // Handle objects with id property (EmployeeUser objects)
    if (item && typeof item === 'object' && item.id) {
      return item.id;
    }
    // Handle SelectOptions format
    if (item && typeof item === 'object' && item.value) {
      return typeof item.value === 'string' ? item.value : item.value.id;
    }
    // Fallback
    return item?.toString?.() || '';
  }).filter(Boolean); // Remove empty values
};

/**
 * Sanitizes JobRequisition data to ensure hiringManager and internalRecruiter
 * are always string arrays (IDs only) before sending to the API
 */
export const sanitizeJobRequisition = (jobRequisition: JobRequisition): JobRequisition & {
  hiringManager: string[];
  internalRecruiter: string[];
  deadline: string;
} => {
  return {
    ...jobRequisition,
    hiringManager: extractStringIds(jobRequisition.hiringManager as any[]),
    internalRecruiter: extractStringIds(
      jobRequisition.internalRecruiter as any[],
    ),
    deadline: formatDeadlineDate(jobRequisition.deadline),
    documentConfigurations: jobRequisition.documentConfigurations?.filter(
      (config) => config.requirement !== 'Disabled',
    ),
  };
};

/**
 * Validates that JobRequisition data has the correct structure for API submission
 */
export const validateJobRequisitionForAPI = (jobRequisition: JobRequisition): boolean => {
  try {
    const sanitized = sanitizeJobRequisition(jobRequisition);

    // Check that hiringManager and internalRecruiter are arrays of strings
    const hiringManagerValid =
      Array.isArray(sanitized.hiringManager) &&
      sanitized.hiringManager.every(
        (item: string) => typeof item === 'string' && item.length > 0,
      );

    const internalRecruiterValid =
      Array.isArray(sanitized.internalRecruiter) &&
      sanitized.internalRecruiter.every(
        (item: string) => typeof item === 'string' && item.length > 0,
      );

    return hiringManagerValid && internalRecruiterValid;
  } catch (error) {
    return false;
  }
};
